import { useCallback, useState, useEffect, useMemo } from 'react';
import { CheckedState } from '@radix-ui/react-checkbox';
import { ListItem } from '@frontend/app/refresh-components/DraggableList';
import { AdsColumnDefinitionInput } from '../ads-table-column-config';

export interface AdsSocialColumn {
  id: string;
  header: string;
  hidden?: boolean;
  subColumns?: AdsSocialColumn[];
  isFixed?: boolean;
  order?: number;
}

interface UseAdsColumnDialogConfig {
  selectedColumns: AdsColumnDefinitionInput[];
  onSave: (columns: string[]) => Promise<void>;
}

const toggleAllChildren = (subColumns: AdsSocialColumn[], shouldHide: boolean): AdsSocialColumn[] => {
  return subColumns.map((sub) => ({
    ...sub,
    hidden: shouldHide,
    subColumns: sub.subColumns ? toggleAllChildren(sub.subColumns, shouldHide) : sub.subColumns,
  }));
};

export const useAdsColumnDialog = ({ selectedColumns, onSave }: UseAdsColumnDialogConfig) => {
  const [editModeColumns, setEditModeColumns] = useState<AdsSocialColumn[]>([]);

  // Convert AdsColumnDefinitionInput to AdsSocialColumn format
  const convertToSocialColumns = useCallback((columns: AdsColumnDefinitionInput[]): AdsSocialColumn[] => {
    return columns.map((col) => ({
      id: col.id,
      header: col.header,
      hidden: col.hidden,
      isFixed: col.isFixed,
      order: col.order,
      subColumns: col.subColumns ? convertToSocialColumns(col.subColumns) : undefined,
    }));
  }, []);

  useEffect(() => {
    setEditModeColumns(convertToSocialColumns(selectedColumns));
  }, [selectedColumns, convertToSocialColumns]);

  const defaultSelectedColumns = useMemo(() => {
    return convertToSocialColumns(selectedColumns);
  }, [selectedColumns, convertToSocialColumns]);

  // Manage dialog open state
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const openDialog = useCallback(() => setIsDialogOpen(true), []);

  const saveColumns = useCallback(
    async (columns: AdsSocialColumn[]) => {
      const visibleColumnIds = columns
        .filter((col: AdsSocialColumn) => !col.hidden)
        .map((col: AdsSocialColumn) => col.id);
      await onSave(visibleColumnIds);
    },
    [onSave],
  );

  const toggleColumnVisibility = useCallback(
    (columns: AdsSocialColumn[], id: string, parentVisible: boolean = true): AdsSocialColumn[] => {
      return columns.map((col) => {
        if (col.id === id) {
          const newHidden = !col.hidden;
          return {
            ...col,
            hidden: newHidden,
            subColumns: col.subColumns ? toggleAllChildren(col.subColumns, newHidden) : col.subColumns,
          };
        }

        if (col.subColumns) {
          const updatedSubColumns = toggleColumnVisibility(col.subColumns, id, !col.hidden);
          const anyChildVisible = updatedSubColumns.some((sub) => !sub.hidden);

          return {
            ...col,
            hidden: anyChildVisible ? false : col.hidden,
            subColumns: updatedSubColumns,
          };
        }

        return {
          ...col,
          hidden: parentVisible ? col.hidden : true,
        };
      });
    },
    [],
  );

  const toggleAllColumnsVisibility = useCallback(
    (columns: AdsSocialColumn[], shouldShow: boolean): AdsSocialColumn[] => {
      const toggleColumns = (cols: AdsSocialColumn[]): AdsSocialColumn[] => {
        return cols.map((col) => ({
          ...col,
          hidden: !col.isFixed && !shouldShow,
          subColumns: col.subColumns ? toggleColumns(col.subColumns as AdsSocialColumn[]) : col.subColumns,
        }));
      };
      return toggleColumns(columns);
    },
    [],
  );

  const onToggleColumnCheckBox = useCallback(
    (id: string) => {
      setEditModeColumns((prevColumns: AdsSocialColumn[]) => toggleColumnVisibility(prevColumns, id));
    },
    [toggleColumnVisibility],
  );

  const onToggleSelectall = useCallback(
    (checked: CheckedState) => {
      setEditModeColumns((prevColumns: AdsSocialColumn[]) => toggleAllColumnsVisibility(prevColumns, checked === true));
    },
    [toggleAllColumnsVisibility],
  );

  const handleDragEndModalColumn = useCallback(
    (items: ListItem<AdsSocialColumn>[]) => {
      if (items.length !== editModeColumns.filter((col) => !col.hidden).length) {
        // Handle item removal case
        return;
      }

      setEditModeColumns((prevColumns: AdsSocialColumn[]) => {
        // Extract the reordered visible columns
        const updatedVisibleColumns = items.map((item) => item.data);

        // Maintain the original positions of hidden columns
        let visibleIndex = 0;
        const updatedColumns = prevColumns.map((col: AdsSocialColumn) => {
          if (col.hidden) {
            return col;
          }
          return updatedVisibleColumns[visibleIndex++];
        });

        return updatedColumns;
      });
    },
    [editModeColumns],
  );

  return {
    columnDialogProps: {
      defaultSelectedColumns,
      selectedColumns: editModeColumns,
      handleDragEndModalColumn,
      onToggleColumnCheckBox,
      onSave: saveColumns,
      onToggleSelectall,
      setSelectedColumns: setEditModeColumns,
      isOpen: isDialogOpen,
      onOpenChange: setIsDialogOpen,
    },
    openDialog,
  };
};
